import React, { useState, useEffect } from 'react';
import {
  FiEdit2,
  FiSlash,
  FiCheckCircle,
  FiKey,
  FiRefreshCcw,
  FiTrash2
} from 'react-icons/fi';
import { getAllUsers, getAllTenants, createUser, updateUser, toggleUserStatus, resetUserPassword } from '../../../services/api';
import './Users.css';

// Interface para o componente Tooltip
interface TooltipProps {
  text: string;
  children?: React.ReactElement;
}

// Componente de Tooltip — aplica também o `title` nativo no filho como
// fallback, evitando clipping em containers com overflow.
const Tooltip: React.FC<TooltipProps> = ({ text, children }) => {
  const child = children
    ? React.cloneElement(children, { title: text })
    : null;

  return (
    <span className="tooltip-container">
      {child}
      <span className="tooltip-text" role="tooltip">{text}</span>
    </span>
  );
};

interface Tenant {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'client';
  tenant: Tenant | null;
  active: boolean;
  lastLogin?: string | null;
  username?: string;
  isTenantAdmin?: boolean;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    role: 'client',
    tenant: '',
    password: '',
    active: true,
    isTenantAdmin: false
  });
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllUsers();
      
      if (response.success) {
        // // console.log('Usuários carregados:', response.data);
        setUsers(response.data || []);
      } else {
        setError(response.error || 'Erro ao carregar usuários');
      }
    } catch (error) {
      setError('Erro ao conectar com o servidor');
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await getAllTenants();
      
      if (response.success) {
        // console.log('Tenants carregados:', response.data);
        setTenants(response.data || []);
      } else {
        console.error('Erro ao carregar tenants:', response.error);
      }
    } catch (error) {
      console.error('Erro ao buscar tenants:', error);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleAddUser = () => {
    setEditUser(null);
    setFormData({
      name: '',
      email: '',
      username: '',
      role: 'client',
      tenant: '',
      password: '',
      active: true,
      isTenantAdmin: false
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleEditUser = (user: User) => {
    // // console.log('Editando usuário:', user);
    setEditUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      username: user.username || '',
      role: user.role,
      tenant: user.tenant ? user.tenant.id : '',
      password: '',
      active: user.active,
      isTenantAdmin: user.isTenantAdmin === true // Use === true para garantir que valores undefined ou null sejam tratados como false
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormLoading(true);

    try {
      // Preparar os dados para enviar à API
      const userData = {
        name: formData.name,
        email: formData.email,
        username: formData.username || formData.email.split('@')[0], // Usar o username informado ou criar um baseado no email
        role: formData.role,
        tenantId: formData.role === 'client' ? formData.tenant : null,
        ...(formData.password && !editUser ? { password: formData.password } : {}),
        active: formData.active,
        isTenantAdmin: formData.role === 'client' ? formData.isTenantAdmin : false
      };

      // // console.log('Enviando dados para API:', userData);

      let response;
      
      if (editUser) {
        // Atualizar usuário existente
        response = await updateUser(editUser.id, userData);
      } else {
        // Criar novo usuário - senha obrigatória para novos usuários
        if (!formData.password) {
          setFormError('A senha é obrigatória para novos usuários');
          setFormLoading(false);
          return;
        }
        
        response = await createUser(userData);
      }

      // // console.log('Resposta da API:', response);

      if (response.success) {
        // Atualizar a lista de usuários
        await fetchUsers();
        setShowModal(false);
      } else {
        setFormError(response.error || 'Erro ao salvar usuário');
        if (response.details) {
          console.error('Detalhes do erro:', response.details);
        }
      }
    } catch (error) {
      setFormError('Erro ao processar a solicitação');
      console.error('Erro ao salvar usuário:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await toggleUserStatus(userId, !currentStatus);
      
      if (response.success) {
        // Atualizar a lista de usuários
        await fetchUsers();
      } else {
        setError(response.error || 'Erro ao alterar status do usuário');
      }
    } catch (error) {
      setError('Erro ao processar a solicitação');
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleResetPassword = async (userId: string, username: string) => {
    if (window.confirm(`Tem certeza que deseja resetar a senha do usuário ${username} para 123456?`)) {
      try {
        const response = await resetUserPassword(userId);
        
        if (response.success) {
          alert('Senha resetada com sucesso para 123456');
        } else {
          setError(response.error || 'Erro ao resetar senha do usuário');
        }
      } catch (error) {
        setError('Erro ao processar a solicitação');
        console.error('Erro ao resetar senha:', error);
      }
    }
  };

  const handleOpenPasswordModal = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError(null);
    setShowPasswordModal(true);
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordLoading(true);

    try {
      // Validar senhas
      if (passwordData.newPassword.length < 6) {
        setPasswordError('A senha deve ter pelo menos 6 caracteres');
        setPasswordLoading(false);
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('As senhas não coincidem');
        setPasswordLoading(false);
        return;
      }
      
      // Em um cenário real, você enviaria a nova senha para a API
      const response = await updateUser(selectedUserId, { password: passwordData.newPassword });
      
      if (response.success) {
        alert('Senha alterada com sucesso');
        setShowPasswordModal(false);
      } else {
        setPasswordError(response.error || 'Erro ao alterar senha');
      }
    } catch (error) {
      setPasswordError('Erro ao processar a solicitação');
      console.error('Erro ao alterar senha:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return <div className="loading">Carregando usuários...</div>;
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Gerenciamento de Usuários</h2>
        <button onClick={handleAddUser} className="btn-add-user">
          Adicionar Usuário
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && users.length === 0 && (
        <div className="no-users">
          Nenhum usuário cadastrado. Clique em "Adicionar Usuário" para criar o primeiro.
        </div>
      )}

      {users.length > 0 && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th>Status</th>
                <th>Último Acesso</th>
                <th style={{ minWidth: "300px", width: "300px" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className={!user.active ? 'inactive-user' : ''}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role} ${user.isTenantAdmin ? 'tenant-admin' : ''}`}>
                      {user.role === 'admin' 
                        ? 'Administrador' 
                        : user.isTenantAdmin 
                          ? 'Admin do Cliente' 
                          : 'Cliente'}
                    </span>
                  </td>
                  <td>{user.tenant ? user.tenant.name : 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${user.active ? 'active' : 'inactive'}`}>
                      {user.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>{typeof user.lastLogin === 'string' ? formatDate(user.lastLogin) : 'Nunca'}</td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <Tooltip text="Editar">
                        <button
                          className="icon-btn btn-edit"
                          onClick={() => handleEditUser(user)}
                          aria-label="Editar"
                        >
                          <FiEdit2 />
                        </button>
                      </Tooltip>

                      <Tooltip text={user.active ? "Desativar" : "Ativar"}>
                        <button
                          className={`icon-btn btn-toggle-status ${user.active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(user.id, user.active)}
                          aria-label={user.active ? "Desativar" : "Ativar"}
                        >
                          {user.active ? <FiSlash /> : <FiCheckCircle />}
                        </button>
                      </Tooltip>

                      <Tooltip text="Alterar Senha">
                        <button
                          className="icon-btn btn-change-password"
                          onClick={() => handleOpenPasswordModal(user.id, user.username || user.name)}
                          aria-label="Alterar Senha"
                        >
                          <FiKey />
                        </button>
                      </Tooltip>

                      <Tooltip text="Resetar Senha">
                        <button
                          className="icon-btn btn-reset-password"
                          onClick={() => handleResetPassword(user.id, user.username || user.name)}
                          aria-label="Resetar Senha"
                        >
                          <FiRefreshCcw />
                        </button>
                      </Tooltip>

                      <Tooltip text="Excluir">
                        <button
                          className="icon-btn btn-delete"
                          aria-label="Excluir"
                          onClick={() => {
                            if (window.confirm(`Tem certeza que deseja excluir o usuário ${user.username || user.name}? Esta ação não pode ser desfeita.`)) {
                              setLoading(true);
                              
                              const token = localStorage.getItem('token');
                              if (!token) {
                                alert('Usuário não autenticado! Faça login novamente.');
                                return;
                              }
                              
                              fetch(`https://getback.digitalsac.io/api/users/${user.id}`, {
                                method: 'DELETE',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                }
                              })
                              .then(response => response.json())
                              .then(data => {
                                if (data.success) {
                                  alert('Usuário excluído com sucesso');
                                  fetchUsers();
                                } else {
                                  alert(`Erro ao excluir usuário: ${data.message || 'Falha na operação'}`);
                                }
                              })
                              .catch(error => {
                                alert('Erro ao tentar excluir usuário: ' + error);
                              })
                              .finally(() => {
                                setLoading(false);
                              });
                            }
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editUser ? 'Editar Usuário' : 'Adicionar Usuário'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nome</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={formLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={formLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="username">Nome de Usuário</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Se não informado, será gerado a partir do email"
                  disabled={formLoading}
                />
              </div>
              {(!editUser || formData.password) && (
                <div className="form-group">
                  <label htmlFor="password">
                    Senha {editUser && <span>(deixe em branco para manter a atual)</span>}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editUser}
                    disabled={formLoading}
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="role">Tipo</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={formLoading}
                >
                  <option value="admin">Administrador</option>
                  <option value="client">Cliente</option>
                </select>
              </div>
              {formData.role === 'client' && (
                <div className="form-group">
                  <label htmlFor="tenant">Cliente (Empresa)</label>
                  <select
                    id="tenant"
                    name="tenant"
                    value={formData.tenant}
                    onChange={handleInputChange}
                    required={formData.role === 'client'}
                    disabled={formLoading}
                  >
                    <option value="">Selecione um cliente</option>
                    {Array.isArray(tenants) && tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group checkbox-group">
                <label htmlFor="active">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    disabled={formLoading}
                  />
                  Ativo
                </label>
              </div>
              
              {formData.role === 'client' && formData.tenant && (
                <div className="form-group checkbox-group">
                  <label htmlFor="isTenantAdmin">
                    <input
                      type="checkbox"
                      id="isTenantAdmin"
                      name="isTenantAdmin"
                      checked={formData.isTenantAdmin}
                      onChange={handleInputChange}
                      disabled={formLoading}
                    />
                    Administrador do Cliente
                  </label>
                  <small className="help-text">
                    Se marcado, este usuário poderá gerenciar outros usuários vinculados a este cliente.
                  </small>
                </div>
              )}
              
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={formLoading}>
                  Cancelar
                </button>
                <button type="submit" className="btn-save" disabled={formLoading}>
                  {formLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Alterar Senha - {selectedUserName}</h3>
              <button className="btn-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="newPassword">Nova Senha</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordInputChange}
                  required
                  disabled={passwordLoading}
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar Senha</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordInputChange}
                  required
                  disabled={passwordLoading}
                  minLength={6}
                />
              </div>
              
              {passwordError && <div className="form-error">{passwordError}</div>}
              
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowPasswordModal(false)} disabled={passwordLoading}>
                  Cancelar
                </button>
                <button type="submit" className="btn-save" disabled={passwordLoading}>
                  {passwordLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users; 